import React, { useEffect, useState, useCallback, Fragment } from 'react'
import styled from 'styled-components'
import { CenterBlock, MediaGrid } from '../styled'
import ProvidersThumbs from './ProvidersThumbs/ProvidersThumbs'
import GameList from './GameList'
import { BREAKPOINTS } from '../themes/breakpoints'
import PropTypes from 'prop-types'
import InfinityScroller from '../utils/InfinityScroller'
import GameService from '../services/GameService'
import Loader from '../utils/Loader'

const Text = styled.p``

const ContentWrapper = styled.div`
    width: 100%;
    height: auto;
    min-height: auto;
    background: #f8f8fa;
    overflow-y: unset;
`

const ProvidersThumbBlock = styled(CenterBlock)`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
`

const StyledThumbBlock = styled(CenterBlock)`
    margin-top: 50px;
    margin-bottom: 50px;
    padding: 0 25px;
    min-height: 300px;
    overflow: hidden;

    @media (max-width: ${BREAKPOINTS.lg + 'px'}) {
        font-size: 12px;
        line-height: 12px;
    }
`

const MediaGridCustom = styled(MediaGrid)`
    @media (max-width: ${BREAKPOINTS.sm + 'px'}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: ${BREAKPOINTS.md + 1 + 'px'}) {
        grid-template-columns: repeat(4, 1fr);
    }
`

const Content = ({
    activeCategory,
    showThumbsByProviders,
    showGamesByProviders,
    thumbClickHandler,
    gameClickHandler,
    setGamesCount = (count) => {},
}) => {
    const [displayedGames, setDisplayedGames] = useState([])
    const [gameIds, setGameIds] = useState([])
    const [liveCasino, setLiveCasino] = useState([])

    const [pageNumber, setPageNumber] = useState(1)
    const [lastPage, setLastPage] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    const loadMore = () => {
        if (pageNumber < lastPage) {
            setIsLoading(true)
            setPageNumber((prev) => {
                return prev + 1
            })
        }
    }

    const onProviderDataUpdate = useCallback((providerData) => {
        setLiveCasino((currentGames) =>
            GameService.updateGamesWithProviderData(currentGames, providerData)
        )
    }, [])

    useEffect(() => {
        if (!activeCategory) return
        const category = activeCategory.url === 'alle' ? '' : activeCategory.url

        // Initial games fetch
        GameService.getLiveCasinoGamesAdapter(
            category,
            pageNumber,
            showGamesByProviders
        ).then(({ games, gameIds, meta }) => {
            setGamesCount(meta.total)
            setLastPage(meta.last_page)
            if (pageNumber === 1) {
                setLiveCasino(games)
                setGameIds(gameIds)
            } else {
                setLiveCasino((prev) => [...prev, ...games])
                setGameIds((prev) => [...prev, ...gameIds])
            }

            setTimeout(() => setIsLoading(false), 1000)
        })
    }, [activeCategory, pageNumber])

    useEffect(() => {
        if (!gameIds.length) return

        // Use GameService's createProviderDataUpdater for handling updates
        const updater = GameService.createProviderDataUpdater(
            gameIds,
            onProviderDataUpdate
        )

        updater.start()
        return () => updater.stop()
    }, [gameIds, onProviderDataUpdate])

    useEffect(() => {
        setDisplayedGames(liveCasino)
    }, [activeCategory, liveCasino])

    useEffect(() => {
        setPageNumber(1)
    }, [activeCategory])

    return (
        <ContentWrapper>
            <Fragment>
                <ProvidersThumbBlock>
                    <ProvidersThumbs
                        activeCategoryName={activeCategory.name}
                        showProviders={showThumbsByProviders}
                        thumbClickHandler={thumbClickHandler}
                        liveCasinoGames={liveCasino}
                    />
                </ProvidersThumbBlock>
                <StyledThumbBlock>
                    <MediaGridCustom>
                        <InfinityScroller
                            maxItems={displayedGames.length}
                            onScrollToBottom={loadMore}
                            isLoading={isLoading}
                        >
                            <GameList
                                games={displayedGames}
                                gameClickHandler={gameClickHandler}
                            />
                        </InfinityScroller>
                    </MediaGridCustom>
                    {isLoading ? (
                        <Loader />
                    ) : (
                        displayedGames.length === 0 &&
                        !isLoading && (
                            <Text align={'center'} pt={3}>
                                Ingen match fundet.
                            </Text>
                        )
                    )}
                </StyledThumbBlock>
            </Fragment>
        </ContentWrapper>
    )
}

Content.propTypes = {
    activeCategory: PropTypes.object.isRequired,
    showThumbsByProviders: PropTypes.arrayOf(PropTypes.string).isRequired,
    showGamesByProviders: PropTypes.arrayOf(PropTypes.string).isRequired,
    thumbClickHandler: PropTypes.func,
    gameClickHandler: PropTypes.func,
    gamesCount: PropTypes.func,
}

export default Content
